"use client";

import { useState, type FC, type Ref } from "react";
import { FormInput, type DataFieldValue } from "@huub-web/ui";
import { isEmpty } from "@huub-web/utils";

import { TemplatesParser } from "../../components";
import type { TemplateCollection } from "../../types";
import { useTemplates } from "../../hooks/useTemplates";

export type ToggledFormInputTemplateProps = {
  /**
   * The unique identifier for this template
   */
  id: string;
  /**
   * The form input ID to listen to for determining the initial toggle state.
   * If the field has a non-empty value, the toggle defaults to "Yes".
   */
  formInputID: string;
  /**
   * The label for the Yes/No toggle
   */
  label?: string;
  /**
   * Child templates to show when "Yes" is selected
   */
  templates?: TemplateCollection;
  /**
   * Ref forwarded to the root element
   */
  ref?: Ref<HTMLDivElement>;
};

/**
 * A wrapper template that displays a Yes/No boolean toggle.
 * Shows child templates when "Yes" is selected.
 * Derives initial state from whether the specified field has a value.
 */
export const ToggledFormInputTemplate: FC<ToggledFormInputTemplateProps> = ({
  id,
  formInputID,
  label,
  templates,
  ref,
}) => {
  const { dataFieldValues, registerDataFieldValue } = useTemplates();

  /**
   * Compute initial boolean state from dataFieldValues.
   * If the field has a non-empty value, default to "Yes" (true).
   */
  const initialValue = (() => {
    const fieldValue = dataFieldValues[formInputID]?.value;
    return !isEmpty(fieldValue);
  })();

  const [isToggled, setIsToggled] = useState<boolean>(initialValue);

  /**
   * Handle toggle change
   */
  const handleChange = (value: DataFieldValue) => {
    const newValue = value === true;
    setIsToggled(newValue);
    registerDataFieldValue(id, newValue);
  };

  return (
    <div ref={ref}>
      <FormInput
        id={id}
        type="boolean"
        label={label}
        value={isToggled}
        onChange={handleChange}
      />
      {isToggled && templates && templates.length > 0 && (
        <TemplatesParser templates={templates} level={1} />
      )}
    </div>
  );
};
